import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { DesktopShell } from '../components/DesktopShell';
import { TopBar } from '../components/TopBar';
import { Card } from '../components/Card';
import { Btn } from '../components/Btn';
import { Pill } from '../components/Pill';
import { Flag } from '../components/Flag';
import { Ic } from '../components/Icon';
import { MatchCardLg } from '../components/MatchCardLg';
import { roomsApi } from '../api/rooms';
import { matchesApi } from '../api/matches';
import { betsApi } from '../api/bets';
import { useAuth } from '../store/auth';
import { teamNamePl } from '../lib/flags';
import { displayNick } from '../lib/nick';
import { phaseLabel } from '../lib/phase';
import { formatKickoff, timeUntil, formatTime, formatDateOnly } from '../lib/date';
import type { Bet, Match, Room } from '../api/types';

export function DashboardPage() {
  const user = useAuth((s) => s.user);
  const location = useLocation();
  const fromUrl = location.pathname + location.search;
  const myRooms = useQuery({ queryKey: ['my-rooms'], queryFn: roomsApi.mine });
  const matches = useQuery({
    queryKey: ['matches'],
    queryFn: () => matchesApi.list(),
    // While anything is live, poll so the score on the dashboard stays fresh.
    refetchInterval: (query) =>
      (query.state.data ?? []).some((m) => m.status === 'LIVE') ? 30_000 : false,
  });

  const allMatches = matches.data ?? [];
  const liveMatches = allMatches.filter((m) => m.status === 'LIVE');
  // "Najbliższe" = mecze, których kickoff jeszcze nie minął, posortowane rosnąco.
  // Mecze z przeszłością bez przełączenia na LIVE/FINISHED (np. brak syncu) nie
  // powinny trafiać do "Najbliższego meczu" ani listy poniżej.
  const now = Date.now();
  const upcomingMatches = allMatches
    .filter((m) => m.status === 'UPCOMING' && new Date(m.kickoff).getTime() > now)
    .sort((a, b) => new Date(a.kickoff).getTime() - new Date(b.kickoff).getTime())
    .slice(0, 5);
  const nextMatch = upcomingMatches[0];
  const allRooms: Room[] = [...(myRooms.data?.owned ?? []), ...(myRooms.data?.member ?? [])];
  const greetingName = user?.nick ? displayNick(user.nick) : 'graczu';

  const nextMatchBet = useQuery({
    queryKey: ['my-bet', nextMatch?.id],
    queryFn: () => betsApi.forMatch(nextMatch!.id),
    enabled: !!nextMatch,
  });

  return (
    <DesktopShell>
      <TopBar
        title={`Cześć, ${greetingName} 👋`}
        subtitle="Twoje pokoje, nadchodzące mecze, ostatnia aktywność."
      />
      <div style={{ flex: 1, padding: '28px 36px', display: 'flex', flexDirection: 'column', gap: 28 }}>
        {nextMatch && (
          <div className="stack-on-mobile" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.2fr) minmax(0,1fr)', gap: 20 }}>
            <Card
              padding={22}
              style={{
                background: 'linear-gradient(135deg,#0f1e3d 0%,#1a2744 100%)',
                color: '#fff',
                border: 'none',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  right: -20,
                  bottom: -30,
                  fontFamily: 'Fraunces, serif',
                  fontSize: 160,
                  fontWeight: 700,
                  color: 'rgba(244,185,58,.08)',
                  lineHeight: 0.8,
                }}
              >
                26
              </div>
              <div
                style={{
                  fontSize: 11,
                  letterSpacing: '.08em',
                  fontWeight: 600,
                  color: 'var(--accent)',
                  textTransform: 'uppercase',
                  marginBottom: 6,
                }}
              >
                MŚ 2026
              </div>
              <div
                className="serif"
                style={{ fontSize: 36, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.1 }}
              >
                Typuj mundial
                <br />
                <span style={{ color: 'rgba(255,255,255,.4)', fontWeight: 500 }}>z ekipą</span>
              </div>
              <div
                style={{
                  fontSize: 13.5,
                  color: 'rgba(255,255,255,.7)',
                  marginTop: 12,
                  maxWidth: 360,
                  lineHeight: 1.5,
                }}
              >
                Trafiony wynik to 3 punkty, sam rezultat to 1.
              </div>
            </Card>

            <NextMatchCard match={nextMatch} myBet={nextMatchBet.data ?? null} />
          </div>
        )}

        {liveMatches.length > 0 && (
          <section>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 14 }}>
              <h3 className="serif" style={{ margin: 0, fontSize: 22, fontWeight: 700, letterSpacing: '-0.01em' }}>
                Na żywo
              </h3>
              <Link to="/calendar">
                <Btn variant="subtle" size="sm">
                  Kalendarz <Ic.chevR size={12} />
                </Btn>
              </Link>
            </div>
            <div
              className="calendar-cards"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                gap: 14,
                maxWidth: 1108,
              }}
            >
              {liveMatches.map((m) => (
                <LiveMatchCard key={m.id} match={m} fromUrl={fromUrl} />
              ))}
            </div>
          </section>
        )}

        <section>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 14 }}>
            <h3 className="serif" style={{ margin: 0, fontSize: 22, fontWeight: 700, letterSpacing: '-0.01em' }}>
              Twoje pokoje
            </h3>
            <Link to="/rooms">
              <Btn variant="subtle" size="sm">
                Wszystkie <Ic.chevR size={12} />
              </Btn>
            </Link>
          </div>

          {allRooms.length === 0 ? (
            <Card padding={24}>
              <div style={{ textAlign: 'center', color: '#5b6578' }}>
                <p>Nie należysz jeszcze do żadnego pokoju.</p>
                <Link to="/rooms/new">
                  <Btn variant="accent">Utwórz swój pierwszy pokój</Btn>
                </Link>
              </div>
            </Card>
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: 14,
              }}
            >
              {allRooms.slice(0, 6).map((r) => (
                <Link key={r.id} to={`/rooms/${r.id}`} style={{ textDecoration: 'none' }}>
                  <RoomCardSm room={r} />
                </Link>
              ))}
            </div>
          )}
        </section>

        <section>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 14 }}>
            <h3 className="serif" style={{ margin: 0, fontSize: 22, fontWeight: 700, letterSpacing: '-0.01em' }}>
              Najbliższe mecze
            </h3>
            <Link to="/calendar">
              <Btn variant="subtle" size="sm">
                Kalendarz <Ic.chevR size={12} />
              </Btn>
            </Link>
          </div>
          <Card padding={0}>
            {upcomingMatches.length === 0 && (
              <div style={{ padding: 24, textAlign: 'center', color: '#5b6578' }}>
                Brak nadchodzących meczów.
              </div>
            )}
            {upcomingMatches.map((m, i) => (
              <Link
                key={m.id}
                to={`/bet/${m.id}`}
                style={{
                  display: 'block',
                  textDecoration: 'none',
                  borderTop: i === 0 ? 'none' : '1px solid rgba(15,30,61,.06)',
                }}
              >
                <MiniMatch match={m} />
              </Link>
            ))}
          </Card>
        </section>
      </div>
    </DesktopShell>
  );
}

function NextMatchCard({ match, myBet }: { match: Match; myBet: Bet | null }) {
  const kickoffPassed = new Date(match.kickoff) <= new Date();
  const canEdit = !kickoffPassed;
  return (
    <Card padding={22} style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div
            style={{
              fontSize: 11,
              color: '#5b6578',
              letterSpacing: '.08em',
              fontWeight: 600,
              textTransform: 'uppercase',
            }}
          >
            Najbliższy mecz
          </div>
          <Pill color="accent">
            <Ic.clock size={10} /> {timeUntil(match.kickoff)}
          </Pill>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 18, margin: '8px 0 6px' }}>
          <div style={{ flex: 1, textAlign: 'left' }}>
            <Flag code={match.homeTeam} size={52} />
            <div className="serif" style={{ fontWeight: 700, fontSize: 18, marginTop: 8 }}>
              {teamNamePl(match.homeTeam)}
            </div>
          </div>
          <div style={{ textAlign: 'center', color: '#5b6578' }}>
            <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '.05em' }}>
              {phaseLabel(match.phase, match.group).toUpperCase()}
            </div>
            <div className="serif" style={{ fontSize: 22, fontWeight: 700, color: '#0f1e3d', marginTop: 2 }}>
              vs
            </div>
            <div style={{ fontSize: 11, marginTop: 2 }}>{formatKickoff(match.kickoff)}</div>
            {match.venue && (
              <div style={{ fontSize: 11, marginTop: 2 }}>{match.venue}</div>
            )}
          </div>
          <div style={{ flex: 1, textAlign: 'right' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Flag code={match.awayTeam} size={52} />
            </div>
            <div className="serif" style={{ fontWeight: 700, fontSize: 18, marginTop: 8 }}>
              {teamNamePl(match.awayTeam)}
            </div>
          </div>
        </div>
      </div>

      {myBet ? (
        <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            style={{
              flex: 1,
              minWidth: 0,
              padding: '10px 14px',
              background: 'rgba(31,157,85,.08)',
              border: '1px solid rgba(31,157,85,.18)',
              borderRadius: 10,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 13,
              color: '#0f6b34',
            }}
          >
            <Ic.check size={14} />
            <span style={{ fontWeight: 600 }}>Twój typ:</span>
            <span className="num" style={{ fontWeight: 700, fontSize: 15 }}>
              {myBet.homeScore} : {myBet.awayScore}
            </span>
          </div>
          {canEdit && (
            <Link to={`/bet/${match.id}`} style={{ textDecoration: 'none', flexShrink: 0 }}>
              <Btn variant="ghost" size="md">Zmień typ</Btn>
            </Link>
          )}
        </div>
      ) : (
        <Link to={`/bet/${match.id}`} style={{ textDecoration: 'none' }}>
          <Btn variant="accent" size="lg" fullWidth style={{ marginTop: 14 }}>
            Obstaw wynik · {formatKickoff(match.kickoff)}
          </Btn>
        </Link>
      )}
    </Card>
  );
}

function LiveMatchCard({ match, fromUrl }: { match: Match; fromUrl: string }) {
  const myBet = useQuery({
    queryKey: ['my-bet', match.id],
    queryFn: () => betsApi.forMatch(match.id),
  });
  const b = myBet.data;
  return (
    <MatchCardLg
      match={match}
      myBet={b ? { home: b.homeScore, away: b.awayScore, points: b.points } : undefined}
      fromUrl={fromUrl}
    />
  );
}

function RoomCardSm({ room }: { room: Room }) {
  return (
    <Card padding={18}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div
          style={{
            width: 42,
            height: 42,
            borderRadius: 11,
            background: '#f4f3ee',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 20,
            flexShrink: 0,
          }}
        >
          {room.emoji ?? '🏆'}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
            }}
          >
            <div
              style={{
                fontWeight: 700,
                fontSize: 15,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: 0,
              }}
            >
              {room.name}
            </div>
            <div className="num" style={{ fontSize: 12, color: '#5b6578', flexShrink: 0 }}>
              {room.memberCount}/{room.maxMembers}
            </div>
          </div>
          <div style={{ marginTop: 4 }}>
            <Pill color="gray">
              {room.type === 'PRIVATE' ? <Ic.lock size={9} /> : <Ic.globe size={9} />}
              {room.type === 'PRIVATE' ? 'prywatny' : 'publiczny'}
            </Pill>
          </div>
        </div>
        <Ic.chevR size={16} style={{ color: 'rgba(15,30,61,.35)', flexShrink: 0 }} />
      </div>
    </Card>
  );
}

function MiniMatch({ match }: { match: Match }) {
  return (
    <div
      className="mini-match"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '14px 22px',
      }}
    >
      <div className="mini-match-date" style={{ width: 80, fontSize: 12, color: '#5b6578' }}>
        <span className="mini-match-date-day">{formatDateOnly(match.kickoff)}</span>
        <span className="mini-match-date-sep"> · </span>
        <span className="mini-match-date-time">{formatTime(match.kickoff)}</span>
      </div>
      <Flag code={match.homeTeam} size={22} />
      <div className="mini-match-team-home" style={{ flex: 1, fontWeight: 500, fontSize: 13, color: '#0f1e3d', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {teamNamePl(match.homeTeam)}
      </div>
      <div className="num mini-match-sep" style={{ fontWeight: 700, fontSize: 14, minWidth: 36, textAlign: 'center', color: '#5b6578' }}>
        –
      </div>
      <div className="mini-match-team-away" style={{ flex: 1, textAlign: 'right', fontWeight: 500, fontSize: 13, color: '#0f1e3d', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {teamNamePl(match.awayTeam)}
      </div>
      <Flag code={match.awayTeam} size={22} />
    </div>
  );
}
